import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

// /seo-api/ is always relative to the current origin (nginx proxy → seo-generator)
const SEO_API = '/seo-api';

interface PageSummary {
  id: string;
  title: string;
  slug: string;
  meta_description: string;
  category: string;
  generated_at: string;
}

const CATEGORY_META: Record<string, { label: string; icon: string; desc: string }> = {
  'Alcohol Addiction':         { label: 'Alcohol Addiction',       icon: '🍷', desc: 'Understanding and overcoming alcohol use disorder' },
  'Opioid Dependency':         { label: 'Opioid & Opiate Addiction', icon: '💊', desc: 'Opioid addiction, withdrawal, and treatment options' },
  'Substance-Specific Treatment': { label: 'Substance Treatment',   icon: '🔬', desc: 'Treatment approaches for specific substances' },
  'General':                   { label: 'Recovery Resources',      icon: '🌱', desc: 'Guides, advice, and support for recovery' },
};

function getCategoryMeta(cat: string) {
  return CATEGORY_META[cat] ?? { label: cat, icon: '📋', desc: '' };
}

function slugify(str: string) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const SEOResourcePage: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const [pages, setPages] = useState<PageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    fetch(`${SEO_API}/pages?status=published&summary=true&limit=200`)
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.pages || d.data || []);
        setPages(list);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load resources.'); setLoading(false); });
  }, []);

  const containerStyle: React.CSSProperties = {
    maxWidth: '1100px', margin: '0 auto', padding: '40px 20px',
  };

  if (loading) return (
    <div style={containerStyle}>
      <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>Loading resources…</div>
    </div>
  );
  if (error) return <div style={containerStyle}><p style={{ color: '#c62828' }}>{error}</p></div>;

  // Group published pages by category
  const grouped: Record<string, PageSummary[]> = {};
  for (const p of pages) {
    const cat = p.category || 'General';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(p);
  }

  // Order: known categories first, then others
  const categoryOrder = ['Alcohol Addiction', 'Opioid Dependency', 'Substance-Specific Treatment', 'General'];
  const orderedCats = [
    ...categoryOrder.filter(c => grouped[c]),
    ...Object.keys(grouped).filter(c => !categoryOrder.includes(c)),
  ];

  const totalPages = pages.length;

  return (
    <div style={containerStyle}>
      {/* Hero header */}
      <div style={{ marginBottom: '48px' }}>
        <h1 style={{ fontSize: '36px', fontWeight: 900, color: '#1a237e', margin: '0 0 12px' }}>
          Treatment Resources
        </h1>
        <p style={{ fontSize: '17px', color: '#555', maxWidth: '640px', lineHeight: 1.65, margin: 0 }}>
          Expert guides on addiction treatment, recovery paths, and getting help.
          {totalPages > 0 && ` ${totalPages} articles across ${orderedCats.length} topics.`}
        </p>

        {/* Category nav pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '24px' }}>
          {orderedCats.map(cat => {
            const meta = getCategoryMeta(cat);
            return (
              <a
                key={cat}
                href={`#${slugify(cat)}`}
                style={{
                  padding: '7px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 600,
                  background: '#e8eaf6', color: '#1a237e', textDecoration: 'none',
                  display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #c5cae9',
                }}
              >
                {meta.icon} {meta.label} <span style={{ opacity: 0.6 }}>({grouped[cat].length})</span>
              </a>
            );
          })}
        </div>
      </div>

      {pages.length === 0 ? (
        <p style={{ color: '#888', textAlign: 'center', padding: '60px' }}>
          No resources published yet. Check back soon.
        </p>
      ) : (
        orderedCats.map(cat => {
          const meta = getCategoryMeta(cat);
          const catPages = grouped[cat];
          const [featured, ...rest] = catPages;

          return (
            <section key={cat} id={slugify(cat)} style={{ marginBottom: '56px' }}>
              {/* Section header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', borderBottom: '2px solid #e8eaf6', paddingBottom: '12px' }}>
                <span style={{ fontSize: '28px' }}>{meta.icon}</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#1a237e' }}>{meta.label}</h2>
                  {meta.desc && <p style={{ margin: 0, fontSize: '13px', color: '#666', marginTop: '2px' }}>{meta.desc}</p>}
                </div>
              </div>

              {/* Featured card (first article in category) */}
              <a
                href={`/resources/${featured.slug}`}
                style={{
                  display: 'block', background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                  borderRadius: '12px', padding: '28px 32px', marginBottom: '16px',
                  textDecoration: 'none', color: '#fff',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7, marginBottom: '10px' }}>
                  Featured Article
                </div>
                <h3 style={{ margin: '0 0 10px', fontSize: '20px', fontWeight: 800, lineHeight: 1.35 }}>{featured.title}</h3>
                {featured.meta_description && (
                  <p style={{ margin: 0, opacity: 0.85, fontSize: '14px', lineHeight: 1.6 }}>{featured.meta_description}</p>
                )}
                <div style={{ marginTop: '16px', fontSize: '13px', fontWeight: 700, color: '#90caf9' }}>
                  Read article →
                </div>
              </a>

              {/* Grid of remaining articles */}
              {rest.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
                  {rest.map(p => (
                    <a
                      key={p.id}
                      href={`/resources/${p.slug}`}
                      style={{
                        display: 'block', background: '#fff', borderRadius: '10px',
                        padding: '18px 20px', textDecoration: 'none', color: 'inherit',
                        border: '1px solid #e8eaf6', borderLeft: '4px solid #1a237e',
                        transition: 'box-shadow 0.15s',
                      }}
                    >
                      <h4 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 700, color: '#1a237e', lineHeight: 1.35 }}>{p.title}</h4>
                      {p.meta_description && (
                        <p style={{ margin: 0, fontSize: '13px', color: '#666', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {p.meta_description}
                        </p>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
};

export default SEOResourcePage;
